import type { CpgDimension } from '@features/campaigns/subdomains/cpg';
import type { CpgReport } from '@features/campaigns';
import type {
  BorderProps,
  Slide,
  TableCell,
  TableRow,
} from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';
import { formatters as cpgFormatters } from '@features/campaigns/subdomains/cpg';
import { sanitizeOptimization } from '@utils/valueTransformers';
import { themeV2 } from '../../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../../utils/constants-v2';
import { contentSlideHeaderV2 } from '../../../../../utils/slide-header-v2';
import { textStylesV2 } from '../../../../../utils/text-styles-v2';
import type { MinimalCpgDimension } from '../../../slides/dimension-slide';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { sortBy } from '@utils/arrays';

const ROWS_PER_SLIDE = 9;

const NO_BORDER: BorderProps = { type: 'none' };
const BOTTOM_BORDER: BorderProps = {
  type: 'solid',
  color: themeV2.colors.magnetBlue,
  pt: 1,
};

const TABLE_HEADER_OPTIONS: TableCell['options'] = {
  color: themeV2.colors.metricBlue,
  bold: true,
  fontFace: DEFAULT_FONT,
};

type TableField = {
  columnLabel: string;
  getValue: (dimension: MinimalCpgDimension) => string;
  key: string;
};

const tableFields = [
  {
    columnLabel: 'Dimension',
    getValue: dimension => dimension.dimensionValue,
    key: 'dimensionValue',
  },
  {
    columnLabel: 'Impressions',
    getValue: dimension =>
      formatters.impressions(dimension.impressions, { full: true }),
    key: 'impressions',
  },
  {
    columnLabel: 'Unique Users',
    getValue: dimension =>
      formatters.users(dimension.uniqueUsers, { full: true }),
    key: 'uniqueUsers',
  },
  {
    columnLabel: 'Projected Incr. Sales',
    getValue: dimension =>
      formatters.currency(dimension.projectedIncrementalSales, {
        full: true,
        decimals: 0,
      }),
    key: 'projectedIncrementalSales',
  },
  {
    columnLabel: 'Incremental Sales Lift',
    getValue: dimension => formatters.lift(dimension.incrementalSalesLift),
    key: 'incrementalSalesLift',
  },
  {
    columnLabel: 'Total Projected Sales',
    getValue: dimension =>
      formatters.currency(dimension.totalProjectedSales, {
        full: true,
        decimals: 0,
      }),
    key: 'totalProjectedSales',
  },
  {
    columnLabel: 'Avg. Price per Transaction',
    getValue: dimension =>
      formatters.currency(dimension.averagePricePerTransaction),
    key: 'averagePricePerTransaction',
  },
  {
    columnLabel: 'Penetration',
    getValue: dimension =>
      formatters.percentage(dimension.penetration, { decimals: 2 }),
    key: 'penetration',
  },
  {
    columnLabel: 'Avg. Purchase Frequency',
    getValue: dimension =>
      cpgFormatters.formatAveragePurchaseFrequency(
        dimension.averagePurchaseFrequency
      ),
    key: 'averagePurchaseFrequency',
  },
  {
    columnLabel: 'Sales Rate',
    getValue: dimension => formatters.currency(dimension.salesRate),
    key: 'salesRate',
  },
  {
    columnLabel: 'Avg. Total Basket Amount',
    getValue: dimension =>
      formatters.currency(dimension.averageTotalBasketAmount),
    key: 'averageTotalBasketAmount',
  },
  {
    columnLabel: 'Optimization Index',
    getValue: dimension =>
      formatters.number(sanitizeOptimization(dimension.salesLiftIndex), {
        decimals: 2,
      }),
    key: 'salesLiftIndex',
  },
] as const satisfies readonly TableField[];

const generateCell = (
  cell: TableCell,
  showBottomBorder: boolean
): TableCell => ({
  text: cell.text,
  options: {
    valign: 'middle',
    fontFace: DEFAULT_FONT,
    border: [
      NO_BORDER,
      NO_BORDER,
      showBottomBorder ? BOTTOM_BORDER : NO_BORDER,
      NO_BORDER,
    ],
    ...cell.options,
  },
});

const generateRow = (cells: TableCell[], showBottomBorder = true): TableRow =>
  cells.map(cell => generateCell(cell, showBottomBorder));

type SalesSummaryTableV2Props = {
  slideMaster: string;
  dimensions: (MinimalCpgDimension & Pick<CpgDimension, 'dimensionName'>)[];
  overallReport: Pick<
    CpgReport,
    | 'impressions'
    | 'uniqueUsers'
    | 'projectedIncrementalSales'
    | 'incrementalSalesLift'
    | 'totalProjectedSales'
    | 'averagePricePerTransaction'
    | 'penetration'
    | 'averagePurchaseFrequency'
    | 'averageTotalBasketAmount'
    | 'totalSalesRate'
  >;
  excludedColumns: string[];
  customHeaders?: Record<string, string | undefined>;
};

export const salesSummaryTableV2 = (
  props: SalesSummaryTableV2Props
): Slide[] => {
  const filteredTableFields = tableFields.filter(
    field => !props.excludedColumns.includes(field.key)
  );

  const overallDimension: MinimalCpgDimension = {
    ...props.overallReport,
    dimensionValue: 'Overall',
    salesLiftIndex: 1,
    salesRate: props.overallReport.totalSalesRate ?? null,
  };

  const overallRow: TableCell[] = filteredTableFields.map(field => ({
    text: field.getValue(overallDimension),
    options: { bold: true },
  }));

  const columnHeaders: TableCell[] = filteredTableFields.map(field => ({
    text: field.columnLabel,
    options: TABLE_HEADER_OPTIONS,
  }));

  const dimensionsByName = props.dimensions.reduce<
    Record<
      string,
      (MinimalCpgDimension & Pick<CpgDimension, 'dimensionName'>)[]
    >
  >((acc, dimension) => {
    const group = acc[dimension.dimensionName] ?? [];
    group.push(dimension);
    acc[dimension.dimensionName] = group;
    return acc;
  }, {});

  return Object.entries(dimensionsByName).flatMap(
    ([dimensionName, dimensions]) => {
      const sorted = [...dimensions].sort(sortBy('salesLiftIndex', 'desc'));
      const allRows = [
        overallRow,
        ...sorted.map(dimension =>
          filteredTableFields.map(field => ({
            text: field.getValue(dimension),
          }))
        ),
      ];

      const totalPages = Math.max(
        1,
        Math.ceil(allRows.length / ROWS_PER_SLIDE)
      );

      const customHeader = props.customHeaders?.[dimensionName];
      const defaultHeader = `Sales Summary Table - By ${(apiSlugToDimensionName as Record<string, string | undefined>)[dimensionName] ?? dimensionName}`;
      const title = customHeader?.length ? customHeader : defaultHeader;

      return Array.from({ length: totalPages }, (_, pageIndex) => {
        const startIndex = pageIndex * ROWS_PER_SLIDE;
        const pageRows = allRows.slice(startIndex, startIndex + ROWS_PER_SLIDE);
        const pageLabel =
          totalPages > 1 ? ` (${pageIndex + 1}/${totalPages})` : '';

        const tableRows = [
          generateRow(columnHeaders),
          ...pageRows.map((row, index) => {
            const isLastRow = index === pageRows.length - 1;
            return generateRow(row, !isLastRow);
          }),
        ];

        return {
          slideMaster: props.slideMaster,
          tables: [
            {
              tableRows,
              options: {
                x: 0.36,
                y: 1.17,
                w: 12.6,
                h: 1.22,
                rowH: 0.61,
                fontSize: 10,
                ...textStylesV2.contentSlideDefaults,
              },
            },
          ],
          texts: [contentSlideHeaderV2(`${title}${pageLabel}`)],
        };
      });
    }
  );
};
